/**
 * Normalize a stored "offer image" value into a proper data: URI.
 *
 * The offer image is captured client-side via FileReader.readAsDataURL(), so it
 * should always already be a full "data:<mime>;base64,<data>" string. However,
 * values that have round-tripped through the database (MEDIUMTEXT column) can
 * occasionally come back as a Buffer, or (for older/legacy records) as a bare
 * base64 string with no "data:" prefix - either of which makes an <img src>
 * or the offer-to-order attachment conversion silently fail (shows a broken
 * image / generic icon, "Offer image is not in the expected format" errors).
 *
 * This normalizes all of those cases into a usable data URI, or returns null
 * if there's nothing usable to show.
 */
const BASE64_RE = /^[A-Za-z0-9+/]+=*$/;

export const normalizeImageDataUrl = (raw, fallbackMimeType = 'image/jpeg') => {
  if (!raw) return null;

  // mysql2 can return TEXT/MEDIUMTEXT columns as Buffers in some configurations.
  const str = typeof raw === 'string' ? raw : (raw instanceof Uint8Array ? Buffer.from(raw).toString('utf8') : String(raw));
  const trimmed = str.trim();
  if (!trimmed) return null;

  // Data URI with an explicit "data:<mime>;base64," prefix. The payload itself
  // may still contain stray whitespace/newlines (e.g. MIME-style 76-char line
  // wrapping from an older import/migration), which - unlike bare base64 below -
  // was previously passed through unmodified. Embedded raw newlines break both
  // <img src> loading (net::ERR_INVALID_URL) and atob() decoding, so always
  // strip whitespace from the payload before using it.
  const prefixMatch = trimmed.match(/^data:([^;,]+);base64,([\s\S]*)$/i);
  if (prefixMatch) {
    const mimeType = prefixMatch[1];
    const cleanedPayload = prefixMatch[2].replace(/\s/g, '');
    if (!cleanedPayload || !BASE64_RE.test(cleanedPayload)) return null;
    return `data:${mimeType};base64,${cleanedPayload}`;
  }

  // Legacy/raw base64 with no "data:" prefix - strip any stray whitespace/newlines
  // (which can sneak in via copy/paste or DB round-tripping) and re-wrap it.
  const cleaned = trimmed.replace(/\s/g, '');
  if (BASE64_RE.test(cleaned)) {
    return `data:${fallbackMimeType};base64,${cleaned}`;
  }

  return null;
};

/**
 * Parse a normalized data: URI into its mime type and raw base64 payload.
 * Returns null if the input isn't a valid data URI.
 */
export const parseImageDataUrl = (raw, fallbackMimeType = 'image/jpeg') => {
  const dataUrl = normalizeImageDataUrl(raw, fallbackMimeType);
  if (!dataUrl) return null;

  const match = dataUrl.match(/^data:([^;]+);base64,([\s\S]*)$/);
  if (!match) return null;

  return { mimeType: match[1], base64Data: match[2] };
};
